import React from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Check, User, Building2, MapPin, BarChart3, Headphones, Database } from "lucide-react";

import { mockData } from "../mock";

const PlansSection = () => {
  const { links } = mockData.company;
  const plans = [
    {
      id: 1,
      name: "Plano Individual",
      price: "R$50",
      period: "/mês",
      description: "Perfeito para proprietários de um veículo",
      popular: false,
      features: [
        "Rastreamento em tempo real",
        "Histórico de rotas",
        "Relatórios básicos",
        "Suporte via WhatsApp",
        "Alertas e notificações",
        "App mobile incluído"
      ],
      icon: User
    },
    {
      id: 2,
      name: "Plano Empresarial",
      price: "Personalizado",
      period: "",
      description: "Solução completa para frotas empresariais",
      popular: true,
      features: [
        "Painel multi-veículos",
        "Relatórios avançados",
        "API para integração",
        "Dashboard web completo",
        "Suporte prioritário 24/7",
        "Backup em nuvem",
        "Geofencing avançado"
      ],
      icon: Building2
    }
  ];

  return (
    <section className="py-24 px-4 relative">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-yellow-500/5 to-transparent"></div>
      
      <div className="container mx-auto relative z-10">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Escolha seu <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">Plano</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Tecnologia de rastreamento veicular adaptada às suas necessidades
          </p>
        </div>

        {/* Plans grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {plans.map((plan) => {
            const IconComponent = plan.icon;
            return (
              <Card 
                key={plan.id}
                className={`relative bg-gray-900/50 border-gray-700 hover:border-yellow-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-yellow-500/25 transform hover:scale-105 ${
                  plan.popular ? 'ring-2 ring-yellow-500/50' : ''
                }`}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-semibold">
                    Mais Popular
                  </Badge>
                )}
                
                <CardHeader className="text-center pb-6">
                  <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                    <IconComponent className="w-8 h-8 text-black" />
                  </div>
                  
                  <CardTitle className="text-2xl font-bold text-white mb-2">
                    {plan.name}
                  </CardTitle>
                  
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-yellow-400">{plan.price}</span>
                    <span className="text-gray-400">{plan.period}</span>
                  </div>
                  
                  <p className="text-gray-300">{plan.description}</p>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    className={`w-full font-semibold transition-all duration-300 ${
                      plan.popular 
                        ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-black hover:from-yellow-500 hover:to-orange-600 shadow-lg shadow-yellow-500/25 hover:shadow-yellow-500/40'
                        : 'border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black'
                    }`}
                    variant={plan.popular ? "default" : "outline"}
                    size="lg"
                    asChild
                  >
                    <a 
                      href={`${links.whatsapp}?text=Olá! Gostaria de saber mais sobre o ${plan.name}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      {plan.price === "Personalizado" ? "Solicitar Orçamento" : "Assinar Plano"}
                    </a>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Additional info */}
        <div className="text-center mt-12">
          <p className="text-gray-400 mb-4">
            Todos os planos incluem período de teste gratuito de 7 dias
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>Sem taxa de instalação</span>
            </div>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              <span>Relatórios ilimitados</span>
            </div>
            <div className="flex items-center gap-2">
              <Headphones className="w-4 h-4" />
              <span>Suporte em português</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlansSection;